/**
 * Time Utility Functions
 *
 * Handles datetime parsing for the Unified Dashboard.
 *
 * IMPORTANT: The getActiveCalls API returns datetime values from SQL Server
 * with a Z suffix (e.g., "2026-01-18T19:54:38.000Z"), but these are actually
 * Sydney local times, NOT UTC. This utility provides functions to correctly
 * parse these values.
 */

import dayjs from 'dayjs';

/**
 * Parse a datetime string as local time (removes Z suffix if present)
 *
 * The database stores Sydney local time, but the API may add a Z suffix
 * which dayjs would interpret as UTC. This function removes the Z suffix
 * to treat the time as local.
 *
 * @param {string|null|undefined} dateStr - The datetime string to parse
 * @returns {dayjs.Dayjs|null} - Parsed dayjs object or null if invalid
 *
 * @example
 * // API returns "2026-01-18T19:54:38.000Z" but it's actually Sydney local time
 * parseAsLocalTime("2026-01-18T19:54:38.000Z")
 * // Returns dayjs object for 2026-01-18 19:54:38 local time
 */
export const parseAsLocalTime = (dateStr) => {
  if (!dateStr) return null;
  // Remove Z suffix to treat as local time, not UTC
  const cleanedStr = String(dateStr).replace(/Z$/i, '');
  const parsed = dayjs(cleanedStr);
  return parsed.isValid() ? parsed : null;
};

/**
 * Calculate wait time in minutes from a datetime string
 *
 * Uses parseAsLocalTime to correctly handle Z-suffixed datetimes from
 * the getActiveCalls API.
 *
 * @param {string|null|undefined} dateStr - The start/anchor datetime
 * @returns {number|null} - Wait time in minutes, or null if invalid
 */
export const calculateWaitMinutesFromTime = (dateStr) => {
  const parsed = parseAsLocalTime(dateStr);
  if (!parsed) return null;

  const diff = dayjs().diff(parsed, 'minute');
  return Number.isFinite(diff) && diff >= 0 ? diff : null;
};

/**
 * Format wait time in a human-readable format
 *
 * @param {number|null|undefined} minutes - Wait time in minutes
 * @returns {string} - Formatted string like "5m", "1h 30m", or "-"
 */
export const formatWaitMinutes = (minutes) => {
  if (minutes === null || minutes === undefined) return '-';
  const absMinutes = Math.abs(minutes);
  if (absMinutes < 60) return `${absMinutes}m`;
  const hours = Math.floor(absMinutes / 60);
  const remainingMins = absMinutes % 60;
  return `${hours}h ${remainingMins}m`;
};

/**
 * Format wait time in seconds to human-readable format
 *
 * @param {number|null|undefined} seconds - Wait time in seconds
 * @returns {string} - Formatted string like "5m", "1h 30m", or "-"
 */
export const formatWaitSeconds = (seconds) => {
  if (!seconds || seconds <= 0) return '-';
  const minutes = Math.floor(seconds / 60);
  return formatWaitMinutes(minutes);
};
